import axios from 'axios'
import { debounce } from 'lodash-es'
import { computed, shallowRef } from 'vue'

const AUTOSAVE_MS = 3000

// config('market_overviews.media.max_kilobytes'): a larger picture is refused
// here, before PHP's own upload limit would answer with something vaguer.
const MAX_PICTURE_BYTES = 10 * 1024 * 1024

/**
 * RED-317 §8 (PR 4b) — the drawer's working copy of one overview.
 *
 * The draft is a .docx on the server; this holds its projection for the
 * Studio canvas and its sha. Every change goes back with the sha it was made
 * on: the canvas and the title/period fields autosave after 3 s of quiet, and
 * starting over, importing from Word and publishing all say which draft they
 * replace. A 409 (someone else saved meanwhile) stops autosaving and shows
 * the conflict choice: reload their draft, or overwrite it with this one.
 * A change Word would refuse comes back as a 422 on the field it concerns;
 * nothing is written then, and the message stays next to that field. No
 * toasts: every outcome is shown where the admin is looking.
 *
 * The editor hands over a getter, not the document: serialising the whole
 * doc happens only when a save actually fires (the Studio store pattern).
 */
export function useMarketOverviewDraft() {
  const document = shallowRef(null)
  const draft = shallowRef(null)
  const loading = shallowRef(false)
  const loadFailed = shallowRef(false)
  const saveState = shallowRef('clean') // clean | dirty | saving | saved | error
  const conflict = shallowRef(null) // { updatedBy, updatedAt } after a 409
  const errors = shallowRef({})
  const busy = shallowRef(null) // start | import | apply | discard

  let overviewId = null
  let docGetter = null
  let fields = { title: null, period: null }
  let dirtyEpoch = 0
  let ticket = 0
  let inFlight = null // the save on the wire, so flush() can wait for it to land

  const hasDraft = computed(() => draft.value !== null)

  const setErrors = next => {
    errors.value = next
  }

  const errorsFrom = error => {
    const bag = error.response?.data?.errors ?? {}
    return Object.fromEntries(Object.entries(bag).map(([key, messages]) => [key, Array.isArray(messages) ? messages[0] : messages]))
  }

  const conflictFrom = error => {
    const by = error.response?.data?.draft?.updated_by
    conflict.value = {
      updatedBy: by ? `${by.firstname ?? ''} ${by.lastname ?? ''}`.trim() : null,
      updatedAt: error.response?.data?.draft?.updated_at ?? null,
    }
  }

  /** A draft payload from the server: what the canvas shows from now on. */
  const adopt = payload => {
    draft.value = payload.draft ?? null
    document.value = payload
    fields = { title: payload.draft?.title ?? null, period: payload.draft?.period ?? null }
    docGetter = null
    dirtyEpoch = 0
    saveState.value = 'clean'
    conflict.value = null
  }

  const reset = () => {
    ticket++
    scheduleSave.cancel()
    overviewId = null
    document.value = null
    draft.value = null
    loading.value = false
    loadFailed.value = false
    saveState.value = 'clean'
    conflict.value = null
    errors.value = {}
    busy.value = null
    docGetter = null
    fields = { title: null, period: null }
    dirtyEpoch = 0
  }

  const load = async id => {
    const mine = ++ticket
    overviewId = id
    loading.value = true
    loadFailed.value = false
    try {
      const { data } = await axios.get(route('admin.market-overview.document', id), { params: { draft: 1 } })
      if (mine === ticket) {
        // Anything but a draft (a broken response) is a failed load, never an empty editor.
        if (data?.draft?.sha) {
          adopt(data)
        } else {
          loadFailed.value = true
        }
      }
    } catch {
      if (mine === ticket) {
        loadFailed.value = true
      }
    } finally {
      if (mine === ticket) {
        loading.value = false
      }
    }
  }

  const save = (force = false) => {
    const landing = send(force).finally(() => {
      if (inFlight === landing) {
        inFlight = null
      }
    })
    inFlight = landing
    return landing
  }

  const send = async force => {
    if (overviewId === null || draft.value === null) {
      return
    }
    const studioDoc = docGetter ? docGetter() : document.value.doc
    const epoch = dirtyEpoch
    const mine = ticket
    saveState.value = 'saving'
    try {
      const { data } = await axios.put(route('admin.market-overview.draft.update', overviewId), {
        studio_doc: studioDoc,
        title: fields.title,
        period: fields.period,
        base_sha: draft.value.sha,
        force,
      })
      if (mine !== ticket) {
        return
      }
      draft.value = data.draft
      conflict.value = null
      setErrors({})
      // Edits made while the request was in flight stay dirty.
      saveState.value = dirtyEpoch === epoch ? 'saved' : 'dirty'
      if (saveState.value === 'dirty') {
        scheduleSave()
      }
    } catch (error) {
      if (mine !== ticket) {
        return
      }
      if (error.response?.status === 409) {
        conflictFrom(error)
        saveState.value = 'dirty'
      } else if (error.response?.status === 422) {
        setErrors(errorsFrom(error))
        saveState.value = 'error'
      } else {
        saveState.value = 'error'
      }
    }
  }

  const scheduleSave = debounce(() => {
    if (saveState.value === 'dirty' && conflict.value === null) {
      save()
    }
  }, AUTOSAVE_MS)

  const touch = () => {
    dirtyEpoch++
    saveState.value = 'dirty'
    scheduleSave()
  }

  /** From the editor's onUpdate. */
  const markDirty = getter => {
    if (typeof getter === 'function') {
      docGetter = getter
    }
    touch()
  }

  const setField = (name, value) => {
    fields = { ...fields, [name]: value === '' ? null : value }
    if (errors.value[name]) {
      setErrors({ ...errors.value, [name]: undefined })
    }
    touch()
  }

  const field = name => fields[name]

  /**
   * Save now what is pending (before publishing, downloading or closing). True when nothing is left unsaved.
   * A save already on the wire (an autosave that just fired) is awaited first, so Escape during it closes
   * once it lands; edits typed meanwhile then go in one more save.
   */
  const flush = async () => {
    scheduleSave.cancel()
    if (inFlight !== null) {
      await inFlight
      scheduleSave.cancel()
    }
    if (saveState.value === 'dirty' || saveState.value === 'error') {
      if (conflict.value !== null) {
        return false
      }
      await save()
    }
    return saveState.value === 'saved' || saveState.value === 'clean'
  }

  /** "Kirjuta üle": this draft wins over the one saved meanwhile. */
  const overwrite = () => save(true)

  /** "Laadi uuesti": drop the local edits, show the draft as it is now. */
  const reloadAfterConflict = () => load(overviewId)

  const run = async (kind, request) => {
    busy.value = kind
    setErrors({})
    try {
      return await request()
    } catch (error) {
      if (error.response?.status === 409) {
        conflictFrom(error)
      } else if (error.response?.status === 422) {
        setErrors(errorsFrom(error))
      } else {
        setErrors({ action: true })
      }
      return null
    } finally {
      busy.value = null
    }
  }

  /** An empty cell's overview row (idempotent on the server); its id, or null with `errors.action`. */
  const ensureCell = cell =>
    run('start', async () => {
      const response = await axios.post(route('admin.market-overview.cells.store'), {
        county: cell.county ?? null,
        purpose: cell.purpose,
      })
      return response.data.overview.id
    })

  /** Start (or restart) the draft: from = current | county | blank. */
  const start = async (id, from, county = null) => {
    overviewId = id
    const data = await run('start', async () => {
      const response = await axios.post(route('admin.market-overview.draft.start', id), {
        from,
        county,
        base_sha: draft.value?.sha ?? null,
      })
      return response.data
    })
    if (data) {
      adopt(data)
    }
    return data !== null
  }

  const discard = async id => {
    const done = await run('discard', async () => {
      await axios.delete(route('admin.market-overview.draft.destroy', id))
      return true
    })
    if (done) {
      const keep = id
      reset()
      overviewId = keep
    }
    return done === true
  }

  /** "Laadi Wordist", step 1: the outline of the uploaded file, or null. */
  const upload = (id, file) => {
    overviewId = id
    return run('import', async () => {
      const body = new FormData()
      body.append('file', file)
      const response = await axios.post(route('admin.market-overview.import', id), body)
      return response.data
    })
  }

  /**
   * A picture for the canvas (PR 3b): staged by the server until the next
   * autosave compiles it into the draft. { ok: true, picture } with the
   * endpoint's { name, mediaPart, url, widthEmu, heightEmu }, or { ok: false,
   * reason: 'too_large' | 'refused' | 'failed', message } where message is
   * the server's Estonian refusal (422) or null. Not a draft action: the
   * editor's own state (busy, errors) stays as it is.
   */
  const uploadPicture = async (id, file) => {
    if (file.size > MAX_PICTURE_BYTES) {
      return { ok: false, reason: 'too_large', message: null }
    }
    try {
      const body = new FormData()
      body.append('file', file)
      const { data } = await axios.post(route('admin.market-overview.draft.media.store', id), body)
      return { ok: true, picture: data }
    } catch (error) {
      const status = error.response?.status
      if (status === 422) {
        return { ok: false, reason: 'refused', message: errorsFrom(error).file ?? null }
      }
      return { ok: false, reason: status === 413 ? 'too_large' : 'failed', message: null }
    }
  }

  /** "Laadi Wordist", step 2: the picked range becomes the draft. */
  const applyImport = async (id, token, { start: from, end, headingAsTitle }) => {
    overviewId = id
    const data = await run('apply', async () => {
      const response = await axios.post(route('admin.market-overview.import.apply', { marketOverview: id, token }), {
        start: from,
        end,
        heading_as_title: headingAsTitle,
        base_sha: draft.value?.sha ?? null,
      })
      return response.data
    })
    if (data) {
      adopt(data)
    }
    return data !== null
  }

  return {
    document,
    draft,
    hasDraft,
    loading,
    loadFailed,
    saveState,
    conflict,
    errors,
    busy,
    load,
    reset,
    ensureCell,
    markDirty,
    setField,
    field,
    flush,
    overwrite,
    reloadAfterConflict,
    start,
    discard,
    upload,
    applyImport,
    uploadPicture,
  }
}
